import React, { useState, useEffect } from "react";
import DesktopIcon from "./DesktopIcon";
import StartMenu from "./StartMenu";
import VolumeControl from "./VolumeControl";
import { useOpenPage, useWindowManager } from "./WindowManager";
import { APPS } from "../apps";

const TaskbarButton = ({ app, active, onClick }) => (
  <button
    className="relative flex items-center h-full w-[19em] min-w-0 px-3"
    title={app.title}
    onClick={onClick}
  >
    {/* Outer Border */}
    <div
      className={`absolute inset-0 border-[3px] border-solid ${
        active
          ? "border-t-black border-l-black border-b-white border-r-white"
          : "border-t-white border-l-white border-b-black border-r-black"
      }`}
    >
      {/* Inner Border */}
      <div
        className={`absolute inset-0 border-[3px] border-solid ${
          active
            ? "border-t-[#828282] border-l-[#828282] border-b-[#c0c0c0] border-r-[#c0c0c0] bg-checkered"
            : "border-t-[#dfdfdf] border-l-[#dfdfdf] border-b-[#828282] border-r-[#828282] bg-[#c0c0c0]"
        }`}
      ></div>
    </div>

    {/* Content */}
    <div className="relative flex items-center gap-2 min-w-0 text-black text-3xl">
      <img src={app.icon} alt="" className="h-5 w-5 shrink-0" draggable={false} />
      <span className="mt-2 truncate">{app.title}</span>
    </div>
  </button>
);

// Each icon opens an app window (app) or a portfolio page (page). Hidden icons keep their spot and
// assets so they can be brought back by flipping the flag.
const DESKTOP_ICONS = [
  { name: "My Computer", src: "/desktop-icons/my-computer-icon.png", position: "top-[0.8rem] left-[0.8rem]", app: "myComputer" },
  { name: "Aaron Zhu - Portfolio", src: "/desktop-icons/iexplorer-link-icon.png", position: "top-[2.0rem] left-[6.8rem]", app: "portfolio" },
  { name: "Internet Explorer", src: "/desktop-icons/internet-explorer-icon.png", position: "top-[7.5rem] left-[0.8rem]", app: "portfolio", hidden: true },
  { name: "saylo.wmv", src: "/desktop-icons/wmv-icon.png", position: "top-[8.6rem] left-[6.3rem]", app: "videoPlayer" },
  { name: "Documents", src: "/desktop-icons/documents-icon.png", position: "top-[7.5rem] left-[0.8rem]", app: "documents" },
  { name: "Recycle Bin", src: "/desktop-icons/recycle-bin-icon.png", position: "top-[14rem] left-5", app: "recycleBin" },
  { name: "las palomitas     .mp3", src: "/desktop-icons/midi-icon.png", position: "top-[20rem] left-9", app: "mediaPlayer" },
  { name: "readme.txt", src: "/desktop-icons/txt-icon.png", position: "top-9 right-5", app: "notepad" },
  { name: "Minesweeper", src: "/desktop-icons/minesweeper-icon.png", position: "top-[9rem] right-7", app: "minesweeper" },
  { name: "MSN", src: "/desktop-icons/msn-icon.png", position: "top-[16rem] right-6", page: "/contact" },
  { name: "my substack     .url", src: "/desktop-icons/iexplorer-link-icon.png", position: "top-[2.0rem] left-[6.8rem]", hidden: true },
  { name: "Inbox", src: "/desktop-icons/mailbox-icon.png", position: "top-[15.3rem] left-[8rem]", hidden: true },
  { name: "Calendar", src: "/desktop-icons/calendar-icon.png", position: "top-9 right-6", hidden: true },
  { name: "Modem", src: "/desktop-icons/modem-icon.png", position: "top-[27rem] left-6", hidden: true },
];

const DesktopBackground = () => {
  const { state, activate, minimize } = useWindowManager();
  const openPage = useOpenPage();
  const [time, setTime] = useState("");
  const [selectedIcon, setSelectedIcon] = useState(null);


  // Update time every minute
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      let hours = now.getHours();
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const period = hours >= 12 ? "PM" : "AM";

      // Convert hours to 12-hour format
      hours = hours % 12 || 12;

      setTime(`${hours}:${minutes} ${period}`);
    };

    updateTime();
    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="h-screen w-screen bg-[#008080] relative desktop-font"
      onPointerDown={(e) => e.target === e.currentTarget && setSelectedIcon(null)}
    >
      {/* Icons */}
      {DESKTOP_ICONS.filter((icon) => !icon.hidden).map(({ name, src, position, app, page }) => (
        <div key={name} className={`absolute ${position}`}>
          <DesktopIcon
            name={name}
            iconSrc={src}
            selected={selectedIcon === name}
            onSelect={() => setSelectedIcon(name)}
            onOpen={app ? () => activate(app) : page ? () => openPage(page) : undefined}
          />
        </div>
      ))}

      {/* Taskbar */}
      <div className="absolute bottom-0 w-full h-12 bg-[#c0c0c0] flex items-center px-2 z-20">
        {/* Start Button */}
        <StartMenu />

        {/* Open Windows */}
        <div className="flex flex-grow min-w-0 items-center gap-1 h-[2.21em] mr-2">
          {state.open.map((id) => {
            const active = state.focused === id;
            return (
              <TaskbarButton
                key={id}
                app={APPS[id]}
                active={active}
                onClick={() => (active ? minimize(id) : activate(id))}
              />
            );
          })}
        </div>

        {/* Sound Icon and Time Group */}
        <div className="flex items-center px-2 py-1 h-[2.21em] gap-2 border-[3px] border-t-[#808080] border-l-[#808080] border-b-[#FFFFFF] border-r-[#FFFFFF] border-solid">
          {/* Sound Icon */}
          <VolumeControl />

          {/* Time Display */}
          <div className="mt-[0.02em] ml-2 text-black text-2xl leading-none flex items-center time-font">
            {time}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopBackground;
